import { useRef, useState, type MouseEvent, type ReactNode, type RefObject } from 'react'
import { format, parseISO, startOfDay, isBefore, isEqual } from 'date-fns'
import { useActualizarTarea } from '@/hooks/useTareas'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

interface EditableDateCellProps {
  /** Tarea que se actualiza al guardar. No se usa si viene `onGuardar`. */
  tareaId?: string
  /** Fecha límite en formato ISO `yyyy-MM-dd`, o null si no tiene. */
  fechaLimite: string | null
  estado: string
  canEdit: boolean
  /**
   * Etiqueta a mostrar en vez de `dd/MM/yyyy`. La tarjeta del kanban pasa acá su
   * copy relativo ("Vencida hace 3d", "Vence hoy"), que sin esto se perdería (ORR-330).
   */
  formatLabel?: (fecha: Date) => ReactNode
  /** Qué mostrar cuando la tarea no tiene fecha. Por defecto un guion. */
  emptyLabel?: ReactNode
  /** Clases del `<input type="date">` en edición: la tarjeta necesita uno más chico. */
  inputClassName?: string
  /** Guarda la fecha en lugar de actualizar `tareaId`. La fila de un grupo la usa para mover todas sus tareas. */
  onGuardar?: (fecha: string | null) => void
}

/**
 * Celda de fecha límite de una tarea con edición inline (ORR-134).
 * Muestra la fecha completa (`dd/MM/yyyy`); al hacer click (si `canEdit`) la
 * convierte en un `<input type="date">` nativo y guarda al perder foco / Enter.
 *
 * Sin `formatLabel`, `emptyLabel` ni `inputClassName` se comporta igual que siempre:
 * la tabla de tareas no cambia.
 *
 * Una tarea completada no deja editar su fecha.
 */
export function EditableDateCell({
  tareaId,
  fechaLimite,
  estado,
  canEdit,
  formatLabel,
  emptyLabel = '-',
  inputClassName,
  onGuardar,
}: EditableDateCellProps) {
  const [editing, setEditing] = useState(false)
  const cancelRef = useRef(false)
  const actualizar = useActualizarTarea()
  // Se recalcula en cada render a propósito. Con `useMemo(…, [])` el día quedaba congelado al
  // montar, y una tarjeta abierta cruzando la medianoche mostraba el texto del día nuevo con el
  // color del día viejo: "Vencida hace 1d" en el naranja de "Hoy" (lo cazó el review de Codex
  // sobre ORR-330). Comparar dos fechas es barato; congelarlas cuesta una inconsistencia visible.
  const today = startOfDay(new Date())

  const fecha = fechaLimite ? startOfDay(parseISO(`${fechaLimite}T00:00:00`)) : null
  const estaVencida = !!fecha && isBefore(fecha, today) && estado !== 'completada'
  const esHoy = !!fecha && isEqual(fecha, today)
  const editable = canEdit && estado !== 'completada'

  const guardar = (valor: string) => {
    setEditing(false)
    const nuevo = valor || null
    if (nuevo === fechaLimite) return
    if (onGuardar) {
      onGuardar(nuevo)
      return
    }
    if (!tareaId) return
    actualizar.mutate(
      { id: tareaId, datos: { fecha_limite: nuevo } },
      {
        onSuccess: () => toast.success('Fecha actualizada'),
        onError: () => toast.error('Error al actualizar la fecha'),
      }
    )
  }

  if (editing) {
    return (
      <FechaInput
        defaultValue={fechaLimite ?? ''}
        className={inputClassName}
        cancelRef={cancelRef}
        onCancelar={() => setEditing(false)}
        onGuardar={guardar}
      />
    )
  }

  const handleClick = (e: MouseEvent) => {
    if (!editable) return
    e.stopPropagation()
    setEditing(true)
  }

  if (!fecha) {
    return (
      <span
        onClick={handleClick}
        className={cn('text-muted-foreground', editable && 'cursor-pointer hover:text-foreground')}
      >
        {emptyLabel}
      </span>
    )
  }

  return (
    <FechaLabel
      fecha={fecha}
      canEdit={editable}
      estaVencida={estaVencida}
      esHoy={esHoy}
      formatLabel={formatLabel}
      onClick={handleClick}
    />
  )
}

interface FechaLabelProps {
  fecha: Date
  canEdit: boolean
  estaVencida: boolean
  esHoy: boolean
  formatLabel?: (fecha: Date) => ReactNode
  onClick: (e: MouseEvent) => void
}

/** La fecha en modo lectura: color según vencimiento y el copy que elija quien la use. */
function FechaLabel({ fecha, canEdit, estaVencida, esHoy, formatLabel, onClick }: FechaLabelProps) {
  return (
    <span
      onClick={onClick}
      className={cn(
        canEdit && 'cursor-pointer hover:underline',
        estaVencida && 'text-red-600 font-medium',
        esHoy && !estaVencida && 'text-orange-600 font-medium'
      )}
      title={estaVencida ? 'Vencida' : esHoy ? 'Hoy' : undefined}
    >
      {formatLabel ? formatLabel(fecha) : format(fecha, 'dd/MM/yyyy')}
    </span>
  )
}

interface FechaInputProps {
  defaultValue: string
  className?: string
  /** Compartido con el padre: distingue "salí con Escape" de "salí perdiendo el foco". */
  cancelRef: RefObject<boolean>
  onCancelar: () => void
  onGuardar: (valor: string) => void
}

/**
 * El `<input type="date">` del modo edición, aparte para que `EditableDateCell` quede con una
 * sola rama de render por estado. Todo el manejo de teclas vive acá.
 */
function FechaInput({ defaultValue, className, cancelRef, onCancelar, onGuardar }: FechaInputProps) {
  return (
    <Input
      type="date"
      defaultValue={defaultValue}
      autoFocus
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => {
        if (cancelRef.current) {
          cancelRef.current = false
          onCancelar()
          return
        }
        onGuardar(e.target.value)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
        if (e.key === 'Escape') {
          cancelRef.current = true
          e.currentTarget.blur()
        }
      }}
      className={cn('h-8 w-auto', className)}
    />
  )
}
